import React, { FC } from "react";
import { AppPage } from "shared-ts-ui-app-page";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUser } from "shared-ts-utils-authentication";
import { McUser, useMcUserDao } from "firefly-my-chimney-ts-user";
import { AddressAccessCard } from "./AddressAccessCard";
import { UserSettings } from "./UserSettings";
import { useResponsive } from "shared-ts-ui-responsive";
import {
  useApiLogout,
  useUrlLogout,
  useUrlLogoutPath,
} from "shared-ts-ui-identity";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { Property } from "shared-ts-property";
import { createLazyRoute } from "@tanstack/react-router";
import { PushNotificationSubscriptionManagement } from "shared-ts-push-notification";
import { useIsMobile } from "shared-ts-utils-ua-parser";
import { If } from "shared-ts-ui-react";
import { useMutation } from "@tanstack/react-query";

export const UserPage: FC = () => {
  const { t } = useTranslation("my-chimney-ts-translations");
  const responsive = useResponsive();
  const mcUserDao = useMcUserDao();
  const identityLogoutPath = useUrlLogoutPath();
  const apiLogout = useApiLogout();
  const urlLogout = useUrlLogout();
  const isMobile = useIsMobile();
  const user = useUser<McUser>();
  const [confirmDeleteUserOpen, setConfirmDeleteUserOpen] =
    React.useState(false);

  const deleteUserMutation = useMutation({
    mutationFn: () => mcUserDao.delete(),
    onSuccess: () => {
      if (isMobile) {
        urlLogout();
      } else {
        apiLogout();
      }
    },
  });

  const handleLogoutClick = async () => {
    apiLogout();
  };

  const handleDeleteUserClick = async () => {
    setConfirmDeleteUserOpen(true);
  };

  const handConfirmDeleteUser = () => {
    setConfirmDeleteUserOpen(false);
    deleteUserMutation.mutate();
  };

  const handCancelDeleteUser = () => {
    setConfirmDeleteUserOpen(false);
  };

  return (
    <AppPage
      $key={"User"}
      name={t("User")}
      mainPaneProps={{ scrollableContentVertically: true }}
      mainPane={
        <Box>
          <Container maxWidth={"xs"}>
            <Stack direction={"column"} useFlexGap gap={2}>
              <Card>
                <CardHeader title={user.fullName}></CardHeader>
                <CardContent>
                  <PropertiesCard
                    elevation={0}
                    childPropertyProps={{
                      fullWidth: true,
                    }}
                  >
                    <Property
                      name={"username"}
                      label={t("Username")}
                      value={user.name}
                      mode={"read"}
                    />
                  </PropertiesCard>
                  <CardActions sx={{ justifyContent: "center" }}>
                    <Toolbar {...responsive.toolbar}>
                      <If
                        expression={isMobile}
                        then={
                          <form
                            name={"logout"}
                            method={"get"}
                            action={identityLogoutPath}
                            onSubmit={() =>
                              console.log("[Logout] submit fired")
                            }
                          >
                            <Button
                              {...responsive.component}
                              type={"submit"}
                              variant={"outlined"}
                            >
                              {t("Logout")}
                            </Button>
                          </form>
                        }
                        else={
                          <Button
                            {...responsive.component}
                            variant={"outlined"}
                            onClick={handleLogoutClick}
                          >
                            {t("Logout")}
                          </Button>
                        }
                      />
                    </Toolbar>
                  </CardActions>
                </CardContent>
              </Card>

              <UserSettings user={user} />
              {localStorage.getItem("PushNotifications") === "true" && (
                <Card>
                  <CardHeader subheader={"Push Notifications"} />
                  <CardContent>
                    <PushNotificationSubscriptionManagement />
                  </CardContent>
                </Card>
              )}
              <AddressAccessCard user={user} />

              <Card variant={"elevation"} sx={{ pb: 1 }}>
                <CardHeader
                  title={
                    <Typography variant={"subtitle2"}>
                      {t("Delete User")}
                    </Typography>
                  }
                />
                <CardContent>
                  {t("Deleting your user means")}:
                  <ul>
                    <Typography component={"li"}>
                      {t("Delete User Explanation..1", {
                        appName: t("My Chimney"),
                      })}
                    </Typography>
                    <Typography component={"li"}>
                      {t("Delete User Explanation..2")}
                    </Typography>
                  </ul>
                </CardContent>
                <CardActions sx={{ justifyContent: "center" }}>
                  <Button
                    {...responsive.component}
                    variant={"outlined"}
                    color={"error"}
                    onClick={handleDeleteUserClick}
                  >
                    {t("Delete User")}
                  </Button>
                </CardActions>
              </Card>
            </Stack>
          </Container>
          <Dialog open={confirmDeleteUserOpen}>
            <DialogTitle>{t("Delete User")}</DialogTitle>
            <DialogContent>
              <Typography>
                {t("Are you sure you want to delete your user?")}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ gap: 2 }}>
              <Button variant={"outlined"} onClick={handConfirmDeleteUser}>
                {t("Yes")}
              </Button>
              <Button onClick={handCancelDeleteUser}>{t("No")}</Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  );
};

export const UserPageRoute = createLazyRoute("/user")({
  component: UserPage,
});
